import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { isTokenExpired } from "../services/api";

interface User {
  username: string;
  token: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, username: string) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore a stored session, unless the token has already expired.
    const token = localStorage.getItem("admin_token");
    const username = localStorage.getItem("admin_username");

    if (token && username && !isTokenExpired(token)) {
      setUser({ token, username });
    } else {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_username");
    }
    setLoading(false);
  }, []);

  const login = (token: string, username: string) => {
    localStorage.setItem("admin_token", token);
    localStorage.setItem("admin_username", username);
    setUser({ token, username });
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, login, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};
